import React, { useEffect, useState } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminHeader from '../../components/admin/AdminHeader';
import { Settings2Icon, SaveIcon, RefreshCwIcon, ChevronDownIcon, SearchIcon, CheckIcon, XIcon, AlertCircleIcon, InfoIcon, HelpCircleIcon, ToggleLeftIcon, ToggleRightIcon, EyeIcon, EyeOffIcon, LockIcon, UnlockIcon, UserIcon, UsersIcon, BellIcon, MailIcon, GlobeIcon, DollarSignIcon, CreditCardIcon, FileTextIcon, ClockIcon, CalendarIcon, ShieldIcon, KeyIcon, ServerIcon, DatabaseIcon, LayersIcon, SlackIcon, MessageSquareIcon, PhoneIcon, SmartphoneIcon, PaletteIcon, TypeIcon, ImageIcon, LayoutIcon, SlidersIcon, AlertTriangleIcon, CheckCircleIcon, XCircleIcon, UploadCloudIcon, DownloadCloudIcon, LinkIcon, ShareIcon, TrendingUpIcon, BarChartIcon, PieChartIcon, Activity, FolderIcon, FileIcon, MoreHorizontalIcon, CheckSquareIcon, XSquareIcon, ArrowRightIcon, ExternalLinkIcon, AlertOctagonIcon, BuildingIcon } from 'lucide-react';
// Define interfaces for settings
interface Setting {
  id: string;
  name: string;
  description: string;
  value: any;
  type: 'toggle' | 'text' | 'number' | 'select' | 'textarea' | 'color' | 'time' | 'email' | 'password' | 'tags' | 'file';
  options?: string[];
  category: string;
  subcategory: string;
  permission: string;
  scope: 'global' | 'mda' | 'user';
  isRequired?: boolean;
  validation?: string;
  placeholder?: string;
  defaultValue?: any;
  isSensitive?: boolean;
}
interface SettingsCategory {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  subcategories: SettingsSubcategory[];
}
interface SettingsSubcategory {
  id: string;
  name: string;
  description: string;
  permission: string;
}
const Settings: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('general');
  const [activeSubcategory, setActiveSubcategory] = useState('basic');
  const [settings, setSettings] = useState<Setting[]>([]);
  const [filteredSettings, setFilteredSettings] = useState<Setting[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showSaveSuccessMessage, setShowSaveSuccessMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Get admin auth context
  const {
    hasPermission,
    isSuperAdmin,
    getMdaId,
    getMdaName
  } = useAdminAuth();
  // Define settings categories
  const categories: SettingsCategory[] = [{
    id: 'general',
    name: 'General Settings',
    description: 'Basic system configuration and appearance settings',
    icon: <Settings2Icon size={20} data-id="element-3767" />,
    subcategories: [{
      id: 'basic',
      name: 'Basic Information',
      description: 'Configure basic system information and branding',
      permission: 'settings:general:read'
    }, {
      id: 'appearance',
      name: 'Appearance',
      description: 'Customize the look and feel of the platform',
      permission: 'settings:appearance:read'
    }, {
      id: 'localization',
      name: 'Localization',
      description: 'Language, date format, and regional settings',
      permission: 'settings:localization:read'
    }]
  }, {
    id: 'notifications',
    name: 'Notifications',
    description: 'Configure email, SMS, and in-app notifications',
    icon: <BellIcon size={20} data-id="element-3768" />,
    subcategories: [{
      id: 'email',
      name: 'Email Notifications',
      description: 'Configure email templates and delivery settings',
      permission: 'settings:notifications:email:read'
    }, {
      id: 'sms',
      name: 'SMS Notifications',
      description: 'Configure SMS templates and delivery settings',
      permission: 'settings:notifications:sms:read'
    }, {
      id: 'app',
      name: 'In-App Notifications',
      description: 'Configure in-app notification settings',
      permission: 'settings:notifications:app:read'
    }]
  }, {
    id: 'security',
    name: 'Security',
    description: 'Security and authentication settings',
    icon: <ShieldIcon size={20} data-id="element-3769" />,
    subcategories: [{
      id: 'authentication',
      name: 'Authentication',
      description: 'Configure login methods and security policies',
      permission: 'settings:security:authentication:read'
    }, {
      id: 'password',
      name: 'Password Policy',
      description: 'Set password requirements and expiration policies',
      permission: 'settings:security:password:read'
    }, {
      id: 'sessions',
      name: 'Sessions',
      description: 'Configure session timeouts and management',
      permission: 'settings:security:sessions:read'
    }, {
      id: 'audit',
      name: 'Audit Logs',
      description: 'Configure audit logging settings',
      permission: 'settings:security:audit:read'
    }]
  }, {
    id: 'payments',
    name: 'Payment Settings',
    description: 'Configure payment gateways and processing options',
    icon: <CreditCardIcon size={20} data-id="element-3770" />,
    subcategories: [{
      id: 'gateways',
      name: 'Payment Gateways',
      description: 'Configure payment processors and gateways',
      permission: 'settings:payments:gateways:read'
    }, {
      id: 'methods',
      name: 'Payment Methods',
      description: 'Enable or disable different payment methods',
      permission: 'settings:payments:methods:read'
    }, {
      id: 'fees',
      name: 'Fees & Charges',
      description: 'Configure transaction fees and service charges',
      permission: 'settings:payments:fees:read'
    }]
  }, {
    id: 'applications',
    name: 'Application Settings',
    description: 'Configure application processing and workflow settings',
    icon: <FileTextIcon size={20} data-id="element-3771" />,
    subcategories: [{
      id: 'workflow',
      name: 'Workflow',
      description: 'Configure application processing workflows',
      permission: 'settings:applications:workflow:read'
    }, {
      id: 'forms',
      name: 'Forms',
      description: 'Configure application form settings',
      permission: 'settings:applications:forms:read'
    }, {
      id: 'approvals',
      name: 'Approvals',
      description: 'Configure approval processes and requirements',
      permission: 'settings:applications:approvals:read'
    }]
  }, {
    id: 'users',
    name: 'User Settings',
    description: 'Configure user registration and profile settings',
    icon: <UsersIcon size={20} data-id="element-3772" />,
    subcategories: [{
      id: 'registration',
      name: 'Registration',
      description: 'Configure user registration process and requirements',
      permission: 'settings:users:registration:read'
    }, {
      id: 'profiles',
      name: 'User Profiles',
      description: 'Configure user profile fields and requirements',
      permission: 'settings:users:profiles:read'
    }, {
      id: 'roles',
      name: 'Roles & Permissions',
      description: 'Configure user roles and permissions',
      permission: 'settings:users:roles:read'
    }]
  }, {
    id: 'integrations',
    name: 'Integrations',
    description: 'Configure third-party integrations and APIs',
    icon: <LinkIcon size={20} data-id="element-3773" />,
    subcategories: [{
      id: 'apis',
      name: 'API Settings',
      description: 'Configure API keys and endpoints',
      permission: 'settings:integrations:apis:read'
    }, {
      id: 'webhooks',
      name: 'Webhooks',
      description: 'Configure webhook endpoints and events',
      permission: 'settings:integrations:webhooks:read'
    }, {
      id: 'services',
      name: 'External Services',
      description: 'Configure connections to external services',
      permission: 'settings:integrations:services:read'
    }]
  }, {
    id: 'system',
    name: 'System Settings',
    description: 'Advanced system configuration and maintenance',
    icon: <ServerIcon size={20} data-id="element-3774" />,
    subcategories: [{
      id: 'maintenance',
      name: 'Maintenance',
      description: 'Configure system maintenance settings',
      permission: 'settings:system:maintenance:read'
    }, {
      id: 'backup',
      name: 'Backup & Recovery',
      description: 'Configure data backup and recovery settings',
      permission: 'settings:system:backup:read'
    }, {
      id: 'logs',
      name: 'System Logs',
      description: 'Configure system logging settings',
      permission: 'settings:system:logs:read'
    }, {
      id: 'performance',
      name: 'Performance',
      description: 'Configure system performance settings',
      permission: 'settings:system:performance:read'
    }]
  }, {
    id: 'mda',
    name: 'MDA Settings',
    description: 'MDA-specific configuration settings',
    icon: <BuildingIcon size={20} data-id="element-3775" />,
    subcategories: [{
      id: 'profile',
      name: 'MDA Profile',
      description: 'Configure MDA profile information',
      permission: 'settings:mda:profile:read'
    }, {
      id: 'services',
      name: 'Service Settings',
      description: 'Configure MDA service settings',
      permission: 'settings:mda:services:read'
    }, {
      id: 'branding',
      name: 'MDA Branding',
      description: 'Configure MDA branding and appearance',
      permission: 'settings:mda:branding:read'
    }]
  }];
  // Generate mock settings based on categories
  const generateMockSettings = () => {
    const allSettings: Setting[] = [];
    // General - Basic
    allSettings.push({
      id: 'platform_name',
      name: 'Platform Name',
      description: 'The name of the platform displayed to users',
      value: 'Nigerian Government Services Portal',
      type: 'text',
      category: 'general',
      subcategory: 'basic',
      permission: 'settings:general:write',
      scope: 'global',
      isRequired: true
    }, {
      id: 'platform_description',
      name: 'Platform Description',
      description: 'Short description of the platform',
      value: 'A unified portal for accessing Nigerian government services online',
      type: 'textarea',
      category: 'general',
      subcategory: 'basic',
      permission: 'settings:general:write',
      scope: 'global'
    }, {
      id: 'contact_email',
      name: 'Contact Email',
      description: 'Primary contact email for the platform',
      value: 'support@govservices.gov.ng',
      type: 'email',
      category: 'general',
      subcategory: 'basic',
      permission: 'settings:general:write',
      scope: 'global',
      isRequired: true
    }, {
      id: 'contact_phone',
      name: 'Contact Phone',
      description: 'Primary contact phone number',
      value: '+234 800 123 4567',
      type: 'text',
      category: 'general',
      subcategory: 'basic',
      permission: 'settings:general:write',
      scope: 'global'
    }, {
      id: 'support_hours',
      name: 'Support Hours',
      description: 'Hours when support is available',
      value: 'Monday to Friday, 8:00 AM - 5:00 PM',
      type: 'text',
      category: 'general',
      subcategory: 'basic',
      permission: 'settings:general:write',
      scope: 'global'
    });
    // General - Appearance
    allSettings.push({
      id: 'primary_color',
      name: 'Primary Color',
      description: 'Primary brand color for the platform',
      value: '#16a34a',
      type: 'color',
      category: 'general',
      subcategory: 'appearance',
      permission: 'settings:appearance:write',
      scope: 'global'
    }, {
      id: 'secondary_color',
      name: 'Secondary Color',
      description: 'Secondary brand color for the platform',
      value: '#2563eb',
      type: 'color',
      category: 'general',
      subcategory: 'appearance',
      permission: 'settings:appearance:write',
      scope: 'global'
    }, {
      id: 'logo_url',
      name: 'Logo URL',
      description: 'URL to the platform logo image',
      value: 'https://placehold.co/400x200/16a34a/ffffff?text=Nigeria+Gov+Services',
      type: 'text',
      category: 'general',
      subcategory: 'appearance',
      permission: 'settings:appearance:write',
      scope: 'global'
    }, {
      id: 'favicon_url',
      name: 'Favicon URL',
      description: 'URL to the favicon image',
      value: 'https://placehold.co/64x64/16a34a/ffffff?text=NG',
      type: 'text',
      category: 'general',
      subcategory: 'appearance',
      permission: 'settings:appearance:write',
      scope: 'global'
    }, {
      id: 'enable_dark_mode',
      name: 'Enable Dark Mode',
      description: 'Allow users to switch to dark mode',
      value: true,
      type: 'toggle',
      category: 'general',
      subcategory: 'appearance',
      permission: 'settings:appearance:write',
      scope: 'global'
    });
    // General - Localization
    allSettings.push({
      id: 'default_language',
      name: 'Default Language',
      description: 'Default language for the platform',
      value: 'English',
      type: 'select',
      options: ['English', 'Hausa', 'Yoruba', 'Igbo', 'Pidgin'],
      category: 'general',
      subcategory: 'localization',
      permission: 'settings:localization:write',
      scope: 'global'
    }, {
      id: 'enable_multilingual',
      name: 'Enable Multilingual Support',
      description: 'Allow users to switch between languages',
      value: true,
      type: 'toggle',
      category: 'general',
      subcategory: 'localization',
      permission: 'settings:localization:write',
      scope: 'global'
    }, {
      id: 'date_format',
      name: 'Date Format',
      description: 'Format for displaying dates',
      value: 'DD/MM/YYYY',
      type: 'select',
      options: ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'],
      category: 'general',
      subcategory: 'localization',
      permission: 'settings:localization:write',
      scope: 'global'
    }, {
      id: 'time_format',
      name: 'Time Format',
      description: 'Format for displaying times',
      value: '12-hour',
      type: 'select',
      options: ['12-hour', '24-hour'],
      category: 'general',
      subcategory: 'localization',
      permission: 'settings:localization:write',
      scope: 'global'
    }, {
      id: 'timezone',
      name: 'Default Timezone',
      description: 'Default timezone for the platform',
      value: 'Africa/Lagos',
      type: 'select',
      options: ['Africa/Lagos', 'UTC', 'Europe/London', 'America/New_York'],
      category: 'general',
      subcategory: 'localization',
      permission: 'settings:localization:write',
      scope: 'global'
    });
    // Notifications - Email
    allSettings.push({
      id: 'email_sender_name',
      name: 'Email Sender Name',
      description: 'Name displayed as the sender of system emails',
      value: 'Nigerian Government Services',
      type: 'text',
      category: 'notifications',
      subcategory: 'email',
      permission: 'settings:notifications:email:write',
      scope: 'global'
    }, {
      id: 'email_sender_address',
      name: 'Email Sender Address',
      description: 'Email address used to send system emails',
      value: 'noreply@govservices.gov.ng',
      type: 'email',
      category: 'notifications',
      subcategory: 'email',
      permission: 'settings:notifications:email:write',
      scope: 'global',
      isRequired: true
    }, {
      id: 'smtp_host',
      name: 'SMTP Host',
      description: 'SMTP server hostname',
      value: 'smtp.govservices.gov.ng',
      type: 'text',
      category: 'notifications',
      subcategory: 'email',
      permission: 'settings:notifications:email:write',
      scope: 'global'
    }, {
      id: 'smtp_port',
      name: 'SMTP Port',
      description: 'SMTP server port',
      value: 587,
      type: 'number',
      category: 'notifications',
      subcategory: 'email',
      permission: 'settings:notifications:email:write',
      scope: 'global'
    }, {
      id: 'smtp_encryption',
      name: 'SMTP Encryption',
      description: 'Encryption method for SMTP',
      value: 'TLS',
      type: 'select',
      options: ['None', 'SSL', 'TLS'],
      category: 'notifications',
      subcategory: 'email',
      permission: 'settings:notifications:email:write',
      scope: 'global'
    }, {
      id: 'smtp_username',
      name: 'SMTP Username',
      description: 'Username for SMTP authentication',
      value: 'smtp_user',
      type: 'text',
      category: 'notifications',
      subcategory: 'email',
      permission: 'settings:notifications:email:write',
      scope: 'global'
    }, {
      id: 'smtp_password',
      name: 'SMTP Password',
      description: 'Password for SMTP authentication',
      value: '********',
      type: 'password',
      category: 'notifications',
      subcategory: 'email',
      permission: 'settings:notifications:email:write',
      scope: 'global',
      isSensitive: true
    });
    // Notifications - SMS
    allSettings.push({
      id: 'sms_provider',
      name: 'SMS Provider',
      description: 'Provider used for sending SMS notifications',
      value: 'Twilio',
      type: 'select',
      options: ['Twilio', 'Infobip', 'Termii', 'BulkSMS'],
      category: 'notifications',
      subcategory: 'sms',
      permission: 'settings:notifications:sms:write',
      scope: 'global'
    }, {
      id: 'sms_api_key',
      name: 'SMS API Key',
      description: 'API key for SMS provider',
      value: '********',
      type: 'password',
      category: 'notifications',
      subcategory: 'sms',
      permission: 'settings:notifications:sms:write',
      scope: 'global',
      isSensitive: true
    }, {
      id: 'sms_sender_id',
      name: 'SMS Sender ID',
      description: 'Sender ID displayed on SMS messages',
      value: 'GOVSERVICES',
      type: 'text',
      category: 'notifications',
      subcategory: 'sms',
      permission: 'settings:notifications:sms:write',
      scope: 'global'
    }, {
      id: 'enable_sms_notifications',
      name: 'Enable SMS Notifications',
      description: 'Enable sending SMS notifications to users',
      value: true,
      type: 'toggle',
      category: 'notifications',
      subcategory: 'sms',
      permission: 'settings:notifications:sms:write',
      scope: 'global'
    });
    // Notifications - In-App
    allSettings.push({
      id: 'enable_inapp_notifications',
      name: 'Enable In-App Notifications',
      description: 'Enable in-app notifications for users',
      value: true,
      type: 'toggle',
      category: 'notifications',
      subcategory: 'app',
      permission: 'settings:notifications:app:write',
      scope: 'global'
    }, {
      id: 'notification_expiry_days',
      name: 'Notification Expiry (Days)',
      description: 'Number of days before notifications expire',
      value: 30,
      type: 'number',
      category: 'notifications',
      subcategory: 'app',
      permission: 'settings:notifications:app:write',
      scope: 'global'
    }, {
      id: 'max_notifications',
      name: 'Maximum Notifications',
      description: 'Maximum number of notifications to store per user',
      value: 100,
      type: 'number',
      category: 'notifications',
      subcategory: 'app',
      permission: 'settings:notifications:app:write',
      scope: 'global'
    }, {
      id: 'enable_push_notifications',
      name: 'Enable Push Notifications',
      description: 'Enable browser push notifications',
      value: false,
      type: 'toggle',
      category: 'notifications',
      subcategory: 'app',
      permission: 'settings:notifications:app:write',
      scope: 'global'
    });
    // Security - Authentication
    allSettings.push({
      id: 'enable_2fa',
      name: 'Enable Two-Factor Authentication',
      description: 'Require two-factor authentication for users',
      value: true,
      type: 'toggle',
      category: 'security',
      subcategory: 'authentication',
      permission: 'settings:security:authentication:write',
      scope: 'global'
    }, {
      id: 'enforce_2fa_for_admins',
      name: 'Enforce 2FA for Admins',
      description: 'Require two-factor authentication for all administrators',
      value: true,
      type: 'toggle',
      category: 'security',
      subcategory: 'authentication',
      permission: 'settings:security:authentication:write',
      scope: 'global'
    }, {
      id: 'login_attempts',
      name: 'Maximum Login Attempts',
      description: 'Number of failed login attempts before account lockout',
      value: 5,
      type: 'number',
      category: 'security',
      subcategory: 'authentication',
      permission: 'settings:security:authentication:write',
      scope: 'global'
    }, {
      id: 'lockout_duration',
      name: 'Account Lockout Duration (Minutes)',
      description: 'Duration of account lockout after failed login attempts',
      value: 30,
      type: 'number',
      category: 'security',
      subcategory: 'authentication',
      permission: 'settings:security:authentication:write',
      scope: 'global'
    }, {
      id: 'enable_social_login',
      name: 'Enable Social Login',
      description: 'Allow users to login with social media accounts',
      value: false,
      type: 'toggle',
      category: 'security',
      subcategory: 'authentication',
      permission: 'settings:security:authentication:write',
      scope: 'global'
    });
    // Security - Password Policy
    allSettings.push({
      id: 'min_password_length',
      name: 'Minimum Password Length',
      description: 'Minimum number of characters required for passwords',
      value: 8,
      type: 'number',
      category: 'security',
      subcategory: 'password',
      permission: 'settings:security:password:write',
      scope: 'global'
    }, {
      id: 'require_uppercase',
      name: 'Require Uppercase Letters',
      description: 'Require at least one uppercase letter in passwords',
      value: true,
      type: 'toggle',
      category: 'security',
      subcategory: 'password',
      permission: 'settings:security:password:write',
      scope: 'global'
    }, {
      id: 'require_lowercase',
      name: 'Require Lowercase Letters',
      description: 'Require at least one lowercase letter in passwords',
      value: true,
      type: 'toggle',
      category: 'security',
      subcategory: 'password',
      permission: 'settings:security:password:write',
      scope: 'global'
    }, {
      id: 'require_numbers',
      name: 'Require Numbers',
      description: 'Require at least one number in passwords',
      value: true,
      type: 'toggle',
      category: 'security',
      subcategory: 'password',
      permission: 'settings:security:password:write',
      scope: 'global'
    }, {
      id: 'require_special',
      name: 'Require Special Characters',
      description: 'Require at least one special character in passwords',
      value: true,
      type: 'toggle',
      category: 'security',
      subcategory: 'password',
      permission: 'settings:security:password:write',
      scope: 'global'
    }, {
      id: 'password_expiry_days',
      name: 'Password Expiry (Days)',
      description: 'Number of days before passwords expire',
      value: 90,
      type: 'number',
      category: 'security',
      subcategory: 'password',
      permission: 'settings:security:password:write',
      scope: 'global'
    }, {
      id: 'prevent_password_reuse',
      name: 'Prevent Password Reuse',
      description: 'Prevent users from reusing previous passwords',
      value: true,
      type: 'toggle',
      category: 'security',
      subcategory: 'password',
      permission: 'settings:security:password:write',
      scope: 'global'
    });
    // Security - Sessions
    allSettings.push({
      id: 'session_timeout',
      name: 'Session Timeout (Minutes)',
      description: 'Time of inactivity before user is logged out',
      value: 30,
      type: 'number',
      category: 'security',
      subcategory: 'sessions',
      permission: 'settings:security:sessions:write',
      scope: 'global'
    }, {
      id: 'max_concurrent_sessions',
      name: 'Maximum Concurrent Sessions',
      description: 'Maximum number of active sessions per user',
      value: 3,
      type: 'number',
      category: 'security',
      subcategory: 'sessions',
      permission: 'settings:security:sessions:write',
      scope: 'global'
    }, {
      id: 'enforce_single_session',
      name: 'Enforce Single Session',
      description: 'Limit users to one active session at a time',
      value: false,
      type: 'toggle',
      category: 'security',
      subcategory: 'sessions',
      permission: 'settings:security:sessions:write',
      scope: 'global'
    }, {
      id: 'remember_me_duration',
      name: 'Remember Me Duration (Days)',
      description: 'Duration of "Remember Me" cookie',
      value: 30,
      type: 'number',
      category: 'security',
      subcategory: 'sessions',
      permission: 'settings:security:sessions:write',
      scope: 'global'
    });
    // Security - Audit Logs
    allSettings.push({
      id: 'enable_audit_logging',
      name: 'Enable Audit Logging',
      description: 'Log user actions for audit purposes',
      value: true,
      type: 'toggle',
      category: 'security',
      subcategory: 'audit',
      permission: 'settings:security:audit:write',
      scope: 'global'
    }, {
      id: 'audit_log_retention',
      name: 'Audit Log Retention (Days)',
      description: 'Number of days to retain audit logs',
      value: 365,
      type: 'number',
      category: 'security',
      subcategory: 'audit',
      permission: 'settings:security:audit:write',
      scope: 'global'
    }, {
      id: 'log_user_logins',
      name: 'Log User Logins',
      description: 'Log all user login attempts',
      value: true,
      type: 'toggle',
      category: 'security',
      subcategory: 'audit',
      permission: 'settings:security:audit:write',
      scope: 'global'
    }, {
      id: 'log_data_access',
      name: 'Log Data Access',
      description: 'Log all data access by users',
      value: true,
      type: 'toggle',
      category: 'security',
      subcategory: 'audit',
      permission: 'settings:security:audit:write',
      scope: 'global'
    }, {
      id: 'log_admin_actions',
      name: 'Log Admin Actions',
      description: 'Log all actions by administrators',
      value: true,
      type: 'toggle',
      category: 'security',
      subcategory: 'audit',
      permission: 'settings:security:audit:write',
      scope: 'global'
    });
    // Payments - Gateways
    allSettings.push({
      id: 'primary_payment_gateway',
      name: 'Primary Payment Gateway',
      description: 'Default payment gateway for processing payments',
      value: 'Paystack',
      type: 'select',
      options: ['Paystack', 'Flutterwave', 'Interswitch', 'Remita'],
      category: 'payments',
      subcategory: 'gateways',
      permission: 'settings:payments:gateways:write',
      scope: 'global'
    }, {
      id: 'paystack_public_key',
      name: 'Paystack Public Key',
      description: 'Public API key for Paystack',
      value: 'pk_test_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
      type: 'text',
      category: 'payments',
      subcategory: 'gateways',
      permission: 'settings:payments:gateways:write',
      scope: 'global'
    }, {
      id: 'paystack_secret_key',
      name: 'Paystack Secret Key',
      description: 'Secret API key for Paystack',
      value: '********',
      type: 'password',
      category: 'payments',
      subcategory: 'gateways',
      permission: 'settings:payments:gateways:write',
      scope: 'global',
      isSensitive: true
    }, {
      id: 'flutterwave_public_key',
      name: 'Flutterwave Public Key',
      description: 'Public API key for Flutterwave',
      value: 'FLWPUBK_TEST-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx-X',
      type: 'text',
      category: 'payments',
      subcategory: 'gateways',
      permission: 'settings:payments:gateways:write',
      scope: 'global'
    }, {
      id: 'flutterwave_secret_key',
      name: 'Flutterwave Secret Key',
      description: 'Secret API key for Flutterwave',
      value: '********',
      type: 'password',
      category: 'payments',
      subcategory: 'gateways',
      permission: 'settings:payments:gateways:write',
      scope: 'global',
      isSensitive: true
    }, {
      id: 'payment_gateway_mode',
      name: 'Payment Gateway Mode',
      description: 'Operating mode for payment gateways',
      value: 'Test',
      type: 'select',
      options: ['Test', 'Live'],
      category: 'payments',
      subcategory: 'gateways',
      permission: 'settings:payments:gateways:write',
      scope: 'global'
    });
    // Payments - Methods
    allSettings.push({
      id: 'enable_card_payments',
      name: 'Enable Card Payments',
      description: 'Allow payment by credit/debit cards',
      value: true,
      type: 'toggle',
      category: 'payments',
      subcategory: 'methods',
      permission: 'settings:payments:methods:write',
      scope: 'global'
    }, {
      id: 'enable_bank_transfers',
      name: 'Enable Bank Transfers',
      description: 'Allow payment by bank transfers',
      value: true,
      type: 'toggle',
      category: 'payments',
      subcategory: 'methods',
      permission: 'settings:payments:methods:write',
      scope: 'global'
    }, {
      id: 'enable_ussd_payments',
      name: 'Enable USSD Payments',
      description: 'Allow payment by USSD',
      value: true,
      type: 'toggle',
      category: 'payments',
      subcategory: 'methods',
      permission: 'settings:payments:methods:write',
      scope: 'global'
    }, {
      id: 'enable_qr_payments',
      name: 'Enable QR Code Payments',
      description: 'Allow payment by QR code',
      value: false,
      type: 'toggle',
      category: 'payments',
      subcategory: 'methods',
      permission: 'settings:payments:methods:write',
      scope: 'global'
    }, {
      id: 'enable_mobile_money',
      name: 'Enable Mobile Money',
      description: 'Allow payment by mobile money',
      value: true,
      type: 'toggle',
      category: 'payments',
      subcategory: 'methods',
      permission: 'settings:payments:methods:write',
      scope: 'global'
    });
    // Payments - Fees
    allSettings.push({
      id: 'transaction_fee_percentage',
      name: 'Transaction Fee Percentage',
      description: 'Percentage fee charged on transactions',
      value: 1.5,
      type: 'number',
      category: 'payments',
      subcategory: 'fees',
      permission: 'settings:payments:fees:write',
      scope: 'global'
    }, {
      id: 'transaction_fee_cap',
      name: 'Transaction Fee Cap (₦)',
      description: 'Maximum fee charged on any transaction',
      value: 2000,
      type: 'number',
      category: 'payments',
      subcategory: 'fees',
      permission: 'settings:payments:fees:write',
      scope: 'global'
    }, {
      id: 'enable_convenience_fee',
      name: 'Enable Convenience Fee',
      description: 'Charge a convenience fee for online payments',
      value: true,
      type: 'toggle',
      category: 'payments',
      subcategory: 'fees',
      permission: 'settings:payments:fees:write',
      scope: 'global'
    }, {
      id: 'convenience_fee_amount',
      name: 'Convenience Fee Amount (₦)',
      description: 'Fixed convenience fee amount',
      value: 100,
      type: 'number',
      category: 'payments',
      subcategory: 'fees',
      permission: 'settings:payments:fees:write',
      scope: 'global'
    });
    // Applications - Workflow
    allSettings.push({
      id: 'default_processing_time',
      name: 'Default Processing Time (Days)',
      description: 'Default number of days for application processing',
      value: 5,
      type: 'number',
      category: 'applications',
      subcategory: 'workflow',
      permission: 'settings:applications:workflow:write',
      scope: 'global'
    }, {
      id: 'enable_auto_approval',
      name: 'Enable Auto-Approval',
      description: 'Automatically approve eligible applications',
      value: false,
      type: 'toggle',
      category: 'applications',
      subcategory: 'workflow',
      permission: 'settings:applications:workflow:write',
      scope: 'global'
    }, {
      id: 'require_payment_first',
      name: 'Require Payment First',
      description: 'Require payment before application submission',
      value: true,
      type: 'toggle',
      category: 'applications',
      subcategory: 'workflow',
      permission: 'settings:applications:workflow:write',
      scope: 'global'
    }, {
      id: 'enable_application_tracking',
      name: 'Enable Application Tracking',
      description: 'Allow users to track application status',
      value: true,
      type: 'toggle',
      category: 'applications',
      subcategory: 'workflow',
      permission: 'settings:applications:workflow:write',
      scope: 'global'
    }, {
      id: 'application_expiry_days',
      name: 'Application Expiry (Days)',
      description: 'Number of days before incomplete applications expire',
      value: 30,
      type: 'number',
      category: 'applications',
      subcategory: 'workflow',
      permission: 'settings:applications:workflow:write',
      scope: 'global'
    });
    // Applications - Forms
    allSettings.push({
      id: 'enable_form_autosave',
      name: 'Enable Form Autosave',
      description: 'Automatically save form progress',
      value: true,
      type: 'toggle',
      category: 'applications',
      subcategory: 'forms',
      permission: 'settings:applications:forms:write',
      scope: 'global'
    }, {
      id: 'autosave_interval',
      name: 'Autosave Interval (Seconds)',
      description: 'Frequency of form autosaves',
      value: 60,
      type: 'number',
      category: 'applications',
      subcategory: 'forms',
      permission: 'settings:applications:forms:write',
      scope: 'global'
    }, {
      id: 'max_file_upload_size',
      name: 'Maximum File Upload Size (MB)',
      description: 'Maximum size for uploaded files',
      value: 10,
      type: 'number',
      category: 'applications',
      subcategory: 'forms',
      permission: 'settings:applications:forms:write',
      scope: 'global'
    }, {
      id: 'allowed_file_types',
      name: 'Allowed File Types',
      description: 'File types that can be uploaded',
      value: 'pdf,jpg,jpeg,png,doc,docx',
      type: 'text',
      category: 'applications',
      subcategory: 'forms',
      permission: 'settings:applications:forms:write',
      scope: 'global'
    }, {
      id: 'enable_form_validation',
      name: 'Enable Form Validation',
      description: 'Validate form fields before submission',
      value: true,
      type: 'toggle',
      category: 'applications',
      subcategory: 'forms',
      permission: 'settings:applications:forms:write',
      scope: 'global'
    });
    // Applications - Approvals
    allSettings.push({
      id: 'require_multiple_approvers',
      name: 'Require Multiple Approvers',
      description: 'Require approval from multiple staff members',
      value: true,
      type: 'toggle',
      category: 'applications',
      subcategory: 'approvals',
      permission: 'settings:applications:approvals:write',
      scope: 'global'
    }, {
      id: 'min_approvers',
      name: 'Minimum Approvers',
      description: 'Minimum number of approvers required',
      value: 2,
      type: 'number',
      category: 'applications',
      subcategory: 'approvals',
      permission: 'settings:applications:approvals:write',
      scope: 'global'
    }, {
      id: 'enable_rejection_reasons',
      name: 'Enable Rejection Reasons',
      description: 'Require staff to provide reasons for rejections',
      value: true,
      type: 'toggle',
      category: 'applications',
      subcategory: 'approvals',
      permission: 'settings:applications:approvals:write',
      scope: 'global'
    }, {
      id: 'enable_approval_delegation',
      name: 'Enable Approval Delegation',
      description: 'Allow staff to delegate approval authority',
      value: false,
      type: 'toggle',
      category: 'applications',
      subcategory: 'approvals',
      permission: 'settings:applications:approvals:write',
      scope: 'global'
    });
    // Users - Registration
    allSettings.push({
      id: 'enable_open_registration',
      name: 'Enable Open Registration',
      description: 'Allow public user registration',
      value: true,
      type: 'toggle',
      category: 'users',
      subcategory: 'registration',
      permission: 'settings:users:registration:write',
      scope: 'global'
    }, {
      id: 'require_email_verification',
      name: 'Require Email Verification',
      description: 'Require users to verify their email address',
      value: true,
      type: 'toggle',
      category: 'users',
      subcategory: 'registration',
      permission: 'settings:users:registration:write',
      scope: 'global'
    }, {
      id: 'require_phone_verification',
      name: 'Require Phone Verification',
      description: 'Require users to verify their phone number',
      value: true,
      type: 'toggle',
      category: 'users',
      subcategory: 'registration',
      permission: 'settings:users:registration:write',
      scope: 'global'
    }, {
      id: 'verification_expiry',
      name: 'Verification Link Expiry (Hours)',
      description: 'Hours before verification links expire',
      value: 24,
      type: 'number',
      category: 'users',
      subcategory: 'registration',
      permission: 'settings:users:registration:write',
      scope: 'global'
    }, {
      id: 'enable_captcha',
      name: 'Enable CAPTCHA',
      description: 'Use CAPTCHA for registration to prevent bots',
      value: true,
      type: 'toggle',
      category: 'users',
      subcategory: 'registration',
      permission: 'settings:users:registration:write',
      scope: 'global'
    });
    // Users - Profiles
    allSettings.push({
      id: 'required_profile_fields',
      name: 'Required Profile Fields',
      description: 'Fields required in user profiles',
      value: 'name,email,phone,address',
      type: 'tags',
      category: 'users',
      subcategory: 'profiles',
      permission: 'settings:users:profiles:write',
      scope: 'global'
    }, {
      id: 'allow_profile_picture',
      name: 'Allow Profile Pictures',
      description: 'Allow users to upload profile pictures',
      value: true,
      type: 'toggle',
      category: 'users',
      subcategory: 'profiles',
      permission: 'settings:users:profiles:write',
      scope: 'global'
    }, {
      id: 'max_profile_picture_size',
      name: 'Maximum Profile Picture Size (KB)',
      description: 'Maximum size for profile pictures',
      value: 500,
      type: 'number',
      category: 'users',
      subcategory: 'profiles',
      permission: 'settings:users:profiles:write',
      scope: 'global'
    }, {
      id: 'enable_profile_completion',
      name: 'Enable Profile Completion Indicator',
      description: 'Show profile completion percentage to users',
      value: true,
      type: 'toggle',
      category: 'users',
      subcategory: 'profiles',
      permission: 'settings:users:profiles:write',
      scope: 'global'
    });
    // Users - Roles
    allSettings.push({
      id: 'default_user_role',
      name: 'Default User Role',
      description: 'Default role assigned to new users',
      value: 'User',
      type: 'select',
      options: ['User', 'Verified User', 'Business User', 'Admin'],
      category: 'users',
      subcategory: 'roles',
      permission: 'settings:users:roles:write',
      scope: 'global'
    }, {
      id: 'enable_role_requests',
      name: 'Enable Role Requests',
      description: 'Allow users to request role changes',
      value: false,
      type: 'toggle',
      category: 'users',
      subcategory: 'roles',
      permission: 'settings:users:roles:write',
      scope: 'global'
    }, {
      id: 'auto_upgrade_verified_users',
      name: 'Auto-Upgrade Verified Users',
      description: 'Automatically upgrade users with verified identity',
      value: true,
      type: 'toggle',
      category: 'users',
      subcategory: 'roles',
      permission: 'settings:users:roles:write',
      scope: 'global'
    });
    // Integrations - APIs
    allSettings.push({
      id: 'enable_api_access',
      name: 'Enable API Access',
      description: 'Allow external applications to access the API',
      value: true,
      type: 'toggle',
      category: 'integrations',
      subcategory: 'apis',
      permission: 'settings:integrations:apis:write',
      scope: 'global'
    }, {
      id: 'api_rate_limit',
      name: 'API Rate Limit (Requests/Minute)',
      description: 'Maximum API requests per minute',
      value: 60,
      type: 'number',
      category: 'integrations',
      subcategory: 'apis',
      permission: 'settings:integrations:apis:write',
      scope: 'global'
    }, {
      id: 'api_token_expiry',
      name: 'API Token Expiry (Days)',
      description: 'Number of days before API tokens expire',
      value: 30,
      type: 'number',
      category: 'integrations',
      subcategory: 'apis',
      permission: 'settings:integrations:apis:write',
      scope: 'global'
    }, {
      id: 'require_api_key_approval',
      name: 'Require API Key Approval',
      description: 'Require manual approval for new API keys',
      value: true,
      type: 'toggle',
      category: 'integrations',
      subcategory: 'apis',
      permission: 'settings:integrations:apis:write',
      scope: 'global'
    });
    // Integrations - Webhooks
    allSettings.push({
      id: 'enable_webhooks',
      name: 'Enable Webhooks',
      description: 'Allow external services to receive webhook events',
      value: true,
      type: 'toggle',
      category: 'integrations',
      subcategory: 'webhooks',
      permission: 'settings:integrations:webhooks:write',
      scope: 'global'
    }, {
      id: 'webhook_secret_key',
      name: 'Webhook Secret Key',
      description: 'Secret key for webhook signature verification',
      value: '********',
      type: 'password',
      category: 'integrations',
      subcategory: 'webhooks',
      permission: 'settings:integrations:webhooks:write',
      scope: 'global',
      isSensitive: true
    }, {
      id: 'webhook_retry_attempts',
      name: 'Webhook Retry Attempts',
      description: 'Number of times to retry failed webhook deliveries',
      value: 3,
      type: 'number',
      category: 'integrations',
      subcategory: 'webhooks',
      permission: 'settings:integrations:webhooks:write',
      scope: 'global'
    }, {
      id: 'webhook_timeout',
      name: 'Webhook Timeout (Seconds)',
      description: 'Timeout for webhook requests',
      value: 10,
      type: 'number',
      category: 'integrations',
      subcategory: 'webhooks',
      permission: 'settings:integrations:webhooks:write',
      scope: 'global'
    });
    // Integrations - External Services
    allSettings.push({
      id: 'enable_nin_verification',
      name: 'Enable NIN Verification',
      description: 'Enable National ID Number verification',
      value: true,
      type: 'toggle',
      category: 'integrations',
      subcategory: 'services',
      permission: 'settings:integrations:services:write',
      scope: 'global'
    }, {
      id: 'nin_api_key',
      name: 'NIN API Key',
      description: 'API key for NIN verification service',
      value: '********',
      type: 'password',
      category: 'integrations',
      subcategory: 'services',
      permission: 'settings:integrations:services:write',
      scope: 'global',
      isSensitive: true
    }, {
      id: 'enable_bvn_verification',
      name: 'Enable BVN Verification',
      description: 'Enable Bank Verification Number verification',
      value: true,
      type: 'toggle',
      category: 'integrations',
      subcategory: 'services',
      permission: 'settings:integrations:services:write',
      scope: 'global'
    }, {
      id: 'bvn_api_key',
      name: 'BVN API Key',
      description: 'API key for BVN verification service',
      value: '********',
      type: 'password',
      category: 'integrations',
      subcategory: 'services',
      permission: 'settings:integrations:services:write',
      scope: 'global',
      isSensitive: true
    }, {
      id: 'enable_address_verification',
      name: 'Enable Address Verification',
      description: 'Enable address verification service',
      value: false,
      type: 'toggle',
      category: 'integrations',
      subcategory: 'services',
      permission: 'settings:integrations:services:write',
      scope: 'global'
    });
    // System - Maintenance
    allSettings.push({
      id: 'maintenance_mode',
      name: 'Maintenance Mode',
      description: 'Put the system in maintenance mode',
      value: false,
      type: 'toggle',
      category: 'system',
      subcategory: 'maintenance',
      permission: 'settings:system:maintenance:write',
      scope: 'global'
    }, {
      id: 'maintenance_message',
      name: 'Maintenance Message',
      description: 'Message displayed during maintenance',
      value: 'The system is currently undergoing scheduled maintenance. Please check back later.',
      type: 'textarea',
      category: 'system',
      subcategory: 'maintenance',
      permission: 'settings:system:maintenance:write',
      scope: 'global'
    }, {
      id: 'scheduled_maintenance_start',
      name: 'Scheduled Maintenance Start',
      description: 'Start time for scheduled maintenance',
      value: '',
      type: 'text',
      category: 'system',
      subcategory: 'maintenance',
      permission: 'settings:system:maintenance:write',
      scope: 'global'
    }, {
      id: 'scheduled_maintenance_end',
      name: 'Scheduled Maintenance End',
      description: 'End time for scheduled maintenance',
      value: '',
      type: 'text',
      category: 'system',
      subcategory: 'maintenance',
      permission: 'settings:system:maintenance:write',
      scope: 'global'
    }, {
      id: 'allow_admin_access_during_maintenance',
      name: 'Allow Admin Access During Maintenance',
      description: 'Allow administrators to access the system during maintenance',
      value: true,
      type: 'toggle',
      category: 'system',
      subcategory: 'maintenance',
      permission: 'settings:system:maintenance:write',
      scope: 'global'
    });
    // System - Backup & Recovery
    allSettings.push({
      id: 'enable_auto_backup',
      name: 'Enable Automatic Backups',
      description: 'Automatically backup system data',
      value: true,
      type: 'toggle',
      category: 'system',
      subcategory: 'backup',
      permission: 'settings:system:backup:write',
      scope: 'global'
    }, {
      id: 'backup_frequency',
      name: 'Backup Frequency',
      description: 'How often to run automatic backups',
      value: 'Daily',
      type: 'select',
      options: ['Hourly', 'Daily', 'Weekly', 'Monthly'],
      category: 'system',
      subcategory: 'backup',
      permission: 'settings:system:backup:write',
      scope: 'global'
    }, {
      id: 'backup_retention',
      name: 'Backup Retention (Days)',
      description: 'Number of days to retain backups',
      value: 30,
      type: 'number',
      category: 'system',
      subcategory: 'backup',
      permission: 'settings:system:backup:write',
      scope: 'global'
    }, {
      id: 'backup_storage_location',
      name: 'Backup Storage Location',
      description: 'Where to store backups',
      value: 'Cloud Storage',
      type: 'select',
      options: ['Local Storage', 'Cloud Storage', 'Both'],
      category: 'system',
      subcategory: 'backup',
      permission: 'settings:system:backup:write',
      scope: 'global'
    });
    // System - Logs
    allSettings.push({
      id: 'log_level',
      name: 'Log Level',
      description: 'Level of detail for system logs',
      value: 'Info',
      type: 'select',
      options: ['Error', 'Warning', 'Info', 'Debug', 'Trace'],
      category: 'system',
      subcategory: 'logs',
      permission: 'settings:system:logs:write',
      scope: 'global'
    }, {
      id: 'log_retention',
      name: 'Log Retention (Days)',
      description: 'Number of days to retain system logs',
      value: 90,
      type: 'number',
      category: 'system',
      subcategory: 'logs',
      permission: 'settings:system:logs:write',
      scope: 'global'
    }, {
      id: 'enable_error_reporting',
      name: 'Enable Error Reporting',
      description: 'Automatically report errors to administrators',
      value: true,
      type: 'toggle',
      category: 'system',
      subcategory: 'logs',
      permission: 'settings:system:logs:write',
      scope: 'global'
    }, {
      id: 'error_notification_email',
      name: 'Error Notification Email',
      description: 'Email to send error notifications to',
      value: 'errors@govservices.gov.ng',
      type: 'email',
      category: 'system',
      subcategory: 'logs',
      permission: 'settings:system:logs:write',
      scope: 'global'
    });
    // System - Performance
    allSettings.push({
      id: 'enable_caching',
      name: 'Enable Caching',
      description: 'Cache frequently accessed data for performance',
      value: true,
      type: 'toggle',
      category: 'system',
      subcategory: 'performance',
      permission: 'settings:system:performance:write',
      scope: 'global'
    }, {
      id: 'cache_ttl',
      name: 'Cache TTL (Minutes)',
      description: 'Time-to-live for cached items',
      value: 60,
      type: 'number',
      category: 'system',
      subcategory: 'performance',
      permission: 'settings:system:performance:write',
      scope: 'global'
    }, {
      id: 'enable_request_throttling',
      name: 'Enable Request Throttling',
      description: 'Limit request rates to prevent abuse',
      value: true,
      type: 'toggle',
      category: 'system',
      subcategory: 'performance',
      permission: 'settings:system:performance:write',
      scope: 'global'
    }, {
      id: 'max_requests_per_minute',
      name: 'Max Requests Per Minute',
      description: 'Maximum requests allowed per minute per IP',
      value: 100,
      type: 'number',
      category: 'system',
      subcategory: 'performance',
      permission: 'settings:system:performance:write',
      scope: 'global'
    });
    // MDA - Profile
    allSettings.push({
      id: 'mda_name',
      name: 'MDA Name',
      description: 'Official name of the MDA',
      value: getMdaName() || 'Ministry of Finance',
      type: 'text',
      category: 'mda',
      subcategory: 'profile',
      permission: 'settings:mda:profile:write',
      scope: 'mda',
      isRequired: true
    }, {
      id: 'mda_short_name',
      name: 'MDA Short Name/Acronym',
      description: 'Short name or acronym of the MDA',
      value: 'MOF',
      type: 'text',
      category: 'mda',
      subcategory: 'profile',
      permission: 'settings:mda:profile:write',
      scope: 'mda'
    }, {
      id: 'mda_description',
      name: 'MDA Description',
      description: 'Brief description of the MDA',
      value: 'The Ministry of Finance is responsible for fiscal policy and financial management of the government.',
      type: 'textarea',
      category: 'mda',
      subcategory: 'profile',
      permission: 'settings:mda:profile:write',
      scope: 'mda'
    }, {
      id: 'mda_address',
      name: 'MDA Address',
      description: 'Physical address of the MDA',
      value: 'Federal Secretariat Complex, Abuja, Nigeria',
      type: 'textarea',
      category: 'mda',
      subcategory: 'profile',
      permission: 'settings:mda:profile:write',
      scope: 'mda'
    }, {
      id: 'mda_contact_email',
      name: 'MDA Contact Email',
      description: 'Primary contact email for the MDA',
      value: 'info@finance.gov.ng',
      type: 'email',
      category: 'mda',
      subcategory: 'profile',
      permission: 'settings:mda:profile:write',
      scope: 'mda',
      isRequired: true
    }, {
      id: 'mda_contact_phone',
      name: 'MDA Contact Phone',
      description: 'Primary contact phone for the MDA',
      value: '+234 9 870 1234',
      type: 'text',
      category: 'mda',
      subcategory: 'profile',
      permission: 'settings:mda:profile:write',
      scope: 'mda'
    }, {
      id: 'mda_website',
      name: 'MDA Website',
      description: 'Official website of the MDA',
      value: 'https://finance.gov.ng',
      type: 'text',
      category: 'mda',
      subcategory: 'profile',
      permission: 'settings:mda:profile:write',
      scope: 'mda'
    });
    // MDA - Services
    allSettings.push({
      id: 'default_service_processing_time',
      name: 'Default Service Processing Time (Days)',
      description: 'Default processing time for MDA services',
      value: 3,
      type: 'number',
      category: 'mda',
      subcategory: 'services',
      permission: 'settings:mda:services:write',
      scope: 'mda'
    }, {
      id: 'enable_service_rating',
      name: 'Enable Service Rating',
      description: 'Allow users to rate services after completion',
      value: true,
      type: 'toggle',
      category: 'mda',
      subcategory: 'services',
      permission: 'settings:mda:services:write',
      scope: 'mda'
    }, {
      id: 'enable_service_feedback',
      name: 'Enable Service Feedback',
      description: 'Allow users to provide feedback on services',
      value: true,
      type: 'toggle',
      category: 'mda',
      subcategory: 'services',
      permission: 'settings:mda:services:write',
      scope: 'mda'
    }, {
      id: 'service_approval_levels',
      name: 'Service Approval Levels',
      description: 'Number of approval levels for services',
      value: 2,
      type: 'number',
      category: 'mda',
      subcategory: 'services',
      permission: 'settings:mda:services:write',
      scope: 'mda'
    });
    // MDA - Branding
    allSettings.push({
      id: 'mda_logo_url',
      name: 'MDA Logo URL',
      description: 'URL to the MDA logo image',
      value: 'https://placehold.co/400x200/16a34a/ffffff?text=MDA+Logo',
      type: 'text',
      category: 'mda',
      subcategory: 'branding',
      permission: 'settings:mda:branding:write',
      scope: 'mda'
    }, {
      id: 'mda_primary_color',
      name: 'MDA Primary Color',
      description: 'Primary brand color for the MDA',
      value: '#16a34a',
      type: 'color',
      category: 'mda',
      subcategory: 'branding',
      permission: 'settings:mda:branding:write',
      scope: 'mda'
    }, {
      id: 'mda_secondary_color',
      name: 'MDA Secondary Color',
      description: 'Secondary brand color for the MDA',
      value: '#2563eb',
      type: 'color',
      category: 'mda',
      subcategory: 'branding',
      permission: 'settings:mda:branding:write',
      scope: 'mda'
    }, {
      id: 'mda_banner_url',
      name: 'MDA Banner URL',
      description: 'URL to the MDA banner image',
      value: 'https://placehold.co/1200x300/16a34a/ffffff?text=MDA+Banner',
      type: 'text',
      category: 'mda',
      subcategory: 'branding',
      permission: 'settings:mda:branding:write',
      scope: 'mda'
    }, {
      id: 'mda_email_signature',
      name: 'MDA Email Signature',
      description: 'Signature for emails sent by the MDA',
      value: 'Best regards,\nThe Ministry of Finance Team\nFederal Republic of Nigeria',
      type: 'textarea',
      category: 'mda',
      subcategory: 'branding',
      permission: 'settings:mda:branding:write',
      scope: 'mda'
    });
    return allSettings;
  };
  // Load settings based on user role
  useEffect(() => {
    setIsLoading(true);
    // Simulate API call delay
    setTimeout(() => {
      const allSettings = generateMockSettings();
      // Filter settings based on user role and permissions
      let visibleSettings;
      if (isSuperAdmin()) {
        // Super admin can see all settings
        visibleSettings = allSettings;
      } else {
        // MDA admin can only see global settings and their MDA-specific settings
        visibleSettings = allSettings.filter(setting => setting.scope === 'global' && hasPermission(setting.permission) || setting.scope === 'mda' && hasPermission(setting.permission));
      }
      setSettings(visibleSettings);
      setFilteredSettings(visibleSettings);
      setIsLoading(false);
    }, 1000);
  }, [isSuperAdmin, hasPermission]);
  // Filter settings based on search query
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredSettings(settings);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = settings.filter(setting => setting.name.toLowerCase().includes(query) || setting.description.toLowerCase().includes(query) || setting.category.toLowerCase().includes(query) || setting.subcategory.toLowerCase().includes(query));
      setFilteredSettings(filtered);
    }
  }, [searchQuery, settings]);
  // Toggle sidebar
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  // Handle setting value change
  const handleSettingChange = (id: string, value: any) => {
    const updatedSettings = settings.map(setting => {
      if (setting.id === id) {
        return {
          ...setting,
          value
        };
      }
      return setting;
    });
    setSettings(updatedSettings);
    setHasChanges(true);
  };
  // Save settings
  const saveSettings = () => {
    setIsSaving(true);
    setErrorMessage(null);
    // Simulate API call delay
    setTimeout(() => {
      setIsSaving(false);
      setHasChanges(false);
      setShowSaveSuccessMessage(true);
      // Hide success message after 3 seconds
      setTimeout(() => {
        setShowSaveSuccessMessage(false);
      }, 3000);
    }, 1500);
  };
  // Reset settings to default
  const resetSettings = () => {
    setShowRestoreModal(false);
    setIsLoading(true);
    // Simulate API call delay
    setTimeout(() => {
      const allSettings = generateMockSettings();
      // Filter settings based on user role and permissions
      let visibleSettings;
      if (isSuperAdmin()) {
        // Super admin can see all settings
        visibleSettings = allSettings;
      } else {
        // MDA admin can only see global settings and their MDA-specific settings
        visibleSettings = allSettings.filter(setting => setting.scope === 'global' && hasPermission(setting.permission) || setting.scope === 'mda' && hasPermission(setting.permission));
      }
      setSettings(visibleSettings);
      setFilteredSettings(visibleSettings);
      setIsLoading(false);
      setHasChanges(false);
    }, 1500);
  };
  // Toggle dropdown
  const toggleDropdown = (dropdown: string) => {
    if (activeDropdown === dropdown) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(dropdown);
    }
  };
  // Get settings for current category and subcategory
  const getCurrentSettings = () => {
    return filteredSettings.filter(setting => setting.category === activeCategory && setting.subcategory === activeSubcategory);
  };
  // Get current category object
  const getCurrentCategory = () => {
    return categories.find(category => category.id === activeCategory);
  };
  // Get current subcategory object
  const getCurrentSubcategory = () => {
    const category = getCurrentCategory();
    return category?.subcategories.find(subcategory => subcategory.id === activeSubcategory);
  };
  // Check if user has permission to edit current settings
  const canEditCurrentSettings = () => {
    const subcategory = getCurrentSubcategory();
    if (!subcategory) return false;
    const permission = subcategory.permission.replace(':read', ':write');
    return hasPermission(permission);
  };
  // Render setting input based on type
  const renderSettingInput = (setting: Setting) => {
    const canEdit = hasPermission(setting.permission);
    switch (setting.type) {
      case 'toggle':
        return <div className="flex items-center" data-id="element-3776">
            <button onClick={() => handleSettingChange(setting.id, !setting.value)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${setting.value ? 'bg-green-600' : 'bg-gray-200'} ${!canEdit ? 'opacity-60 cursor-not-allowed' : ''}`} disabled={!canEdit} data-id="element-3777">
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${setting.value ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-3778" />
            </button>
            <span className="ml-3 text-sm text-gray-500" data-id="element-3779">
              {setting.value ? 'Enabled' : 'Disabled'}
            </span>
          </div>;
      case 'text':
      case 'email':
        return <input type={setting.type} value={setting.value} onChange={e => handleSettingChange(setting.id, e.target.value)} className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`} placeholder={setting.placeholder} disabled={!canEdit} data-id="element-3780" />;
      case 'password':
        return <div className="relative" data-id="element-3781">
            <input type={activeDropdown === `password-${setting.id}` ? 'text' : 'password'} value={setting.value} onChange={e => handleSettingChange(setting.id, e.target.value)} className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`} placeholder={setting.placeholder} disabled={!canEdit} data-id="element-3782" />
            <button type="button" className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-500" onClick={() => toggleDropdown(`password-${setting.id}`)} disabled={!canEdit} data-id="element-3783">
              {activeDropdown === `password-${setting.id}` ? <EyeOffIcon className="h-5 w-5" data-id="element-3784" /> : <EyeIcon className="h-5 w-5" data-id="element-3785" />}
            </button>
          </div>;
      case 'number':
        return <input type="number" value={setting.value} onChange={e => handleSettingChange(setting.id, parseFloat(e.target.value))} className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`} placeholder={setting.placeholder} disabled={!canEdit} data-id="element-3786" />;
      case 'textarea':
        return <textarea value={setting.value} onChange={e => handleSettingChange(setting.id, e.target.value)} rows={3} className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`} placeholder={setting.placeholder} disabled={!canEdit} data-id="element-3787" />;
      case 'select':
        return <select value={setting.value} onChange={e => handleSettingChange(setting.id, e.target.value)} className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`} disabled={!canEdit} data-id="element-3788">
            {setting.options?.map(option => <option key={option} value={option} data-id="element-3789">
                {option}
              </option>)}
          </select>;
      case 'color':
        return <div className="flex items-center" data-id="element-3790">
            <input type="color" value={setting.value} onChange={e => handleSettingChange(setting.id, e.target.value)} className={`h-8 w-8 rounded-md border-gray-300 ${!canEdit ? 'opacity-60 cursor-not-allowed' : ''}`} disabled={!canEdit} data-id="element-3791" />
            <input type="text" value={setting.value} onChange={e => handleSettingChange(setting.id, e.target.value)} className={`ml-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`} placeholder={setting.placeholder} disabled={!canEdit} data-id="element-3792" />
          </div>;
      case 'tags':
        return <div className="flex flex-wrap gap-2 p-2 border border-gray-300 rounded-md" data-id="element-3793">
            {setting.value.split(',').map((tag, index) => <div key={index} className="flex items-center bg-gray-100 rounded-md px-2 py-1" data-id="element-3794">
                <span className="text-sm" data-id="element-3795">{tag}</span>
                {canEdit && <button type="button" className="ml-1 text-gray-400 hover:text-gray-500" onClick={() => {
              const tags = setting.value.split(',');
              tags.splice(index, 1);
              handleSettingChange(setting.id, tags.join(','));
            }} data-id="element-3796">
                    <XIcon className="h-3 w-3" data-id="element-3797" />
                  </button>}
              </div>)}
            {canEdit && <input type="text" placeholder="Add tag..." className="flex-1 min-w-[100px] border-0 p-0 focus:ring-0 text-sm" onKeyDown={e => {
            if (e.key === 'Enter' && e.currentTarget.value.trim() !== '') {
              e.preventDefault();
              const newTag = e.currentTarget.value.trim();
              const currentTags = setting.value ? setting.value.split(',') : [];
              if (!currentTags.includes(newTag)) {
                handleSettingChange(setting.id, [...currentTags, newTag].join(','));
              }
              e.currentTarget.value = '';
            }
          }} data-id="element-3798" />}
          </div>;
      case 'file':
        return <div className="flex items-center" data-id="element-3799">
            <input type="file" className={`block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-green-50 file:text-green-700 hover:file:bg-green-100 ${!canEdit ? 'cursor-not-allowed' : ''}`} disabled={!canEdit} data-id="element-3800" />
          </div>;
      default:
        return <input type="text" value={setting.value} onChange={e => handleSettingChange(setting.id, e.target.value)} className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`} placeholder={setting.placeholder} disabled={!canEdit} data-id="element-3801" />;
    }
  };
  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-100" data-id="element-3802">
        <div className="flex flex-col items-center" data-id="element-3803">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4" data-id="element-3804"></div>
          <p className="text-gray-600" data-id="element-3805">Loading settings...</p>
        </div>
      </div>;
  }
  return <div className="flex h-screen bg-gray-100" data-id="element-3806">
      {/* Sidebar - hidden on mobile unless toggled */}
      <div className={`${isSidebarOpen ? 'block' : 'hidden'} lg:block fixed inset-y-0 left-0 z-50 lg:relative lg:z-0`} data-id="element-3807">
        <AdminSidebar data-id="element-3808" />
      </div>
      {/* Backdrop for mobile sidebar */}
      {isSidebarOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={() => setIsSidebarOpen(false)} data-id="element-3809"></div>}
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-3810">
        <AdminHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} data-id="element-3811" />
        <main className="flex-1 overflow-y-auto p-4 lg:p-6" data-id="element-3812">
          {/* Page Header */}
          <div className="mb-6" data-id="element-3813">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-3814">
              Settings
              {!isSuperAdmin() && <span className="ml-2 text-lg font-normal text-gray-600" data-id="element-3815">
                  ({getMdaName()})
                </span>}
            </h1>
            <p className="text-gray-600 mt-1" data-id="element-3816">
              {isSuperAdmin() ? 'Configure system-wide and MDA-specific settings' : `Configure settings for ${getMdaName()}`}
            </p>
          </div>
          {/* Settings Actions */}
          <div className="bg-white rounded-lg shadow mb-6" data-id="element-3817">
            <div className="p-4 border-b border-gray-200" data-id="element-3818">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-3819">
                {/* Search */}
                <div className="relative flex-1" data-id="element-3820">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3821">
                    <SearchIcon size={18} className="text-gray-400" data-id="element-3822" />
                  </div>
                  <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500 sm:text-sm" placeholder="Search settings..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-3823" />
                </div>
                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2" data-id="element-3824">
                  <button onClick={saveSettings} disabled={!hasChanges || isSaving} className={`flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${!hasChanges ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`} data-id="element-3825">
                    {isSaving ? <>
                        <RefreshCwIcon size={18} className="mr-2 animate-spin" data-id="element-3826" />
                        Saving...
                      </> : <>
                        <SaveIcon size={18} className="mr-2" data-id="element-3827" />
                        Save Changes
                      </>}
                  </button>
                  <button onClick={() => setShowRestoreModal(true)} className="flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-3828">
                    <RefreshCwIcon size={18} className="mr-2" data-id="element-3829" />
                    Restore Defaults
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* Success Message */}
          {showSaveSuccessMessage && <div className="mb-6 bg-green-50 border-l-4 border-green-500 p-4 rounded-md" data-id="element-3830">
              <div className="flex items-center" data-id="element-3831">
                <CheckCircleIcon size={20} className="text-green-500 mr-2" data-id="element-3832" />
                <p className="text-sm text-green-700" data-id="element-3833">
                  Settings saved successfully.
                </p>
              </div>
            </div>}
          {/* Error Message */}
          {errorMessage && <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-md" data-id="element-3834">
              <div className="flex items-center" data-id="element-3835">
                <XCircleIcon size={20} className="text-red-500 mr-2" data-id="element-3836" />
                <p className="text-sm text-red-700" data-id="element-3837">{errorMessage}</p>
              </div>
            </div>}
          <div className="flex flex-col lg:flex-row gap-6" data-id="element-3838">
            {/* Settings Categories */}
            <div className="lg:w-1/4 xl:w-1/5 space-y-4" data-id="element-3839">
              <div className="bg-white rounded-lg shadow overflow-hidden" data-id="element-3840">
                <div className="p-4 border-b border-gray-200" data-id="element-3841">
                  <h2 className="text-lg font-medium text-gray-900" data-id="element-3842">
                    Categories
                  </h2>
                </div>
                <div className="p-2" data-id="element-3843">
                  <nav className="space-y-1" data-id="element-3844">
                    {categories.map(category => {
                    // Check if user has permission to view any subcategory
                    const hasSubcategoryPermission = category.subcategories.some(subcategory => hasPermission(subcategory.permission));
                    // Skip category if user doesn't have permission for any subcategory
                    if (!hasSubcategoryPermission) return null;
                    return <div key={category.id} className="space-y-1" data-id="element-3845">
                          <button onClick={() => {
                        setActiveCategory(category.id);
                        setActiveSubcategory(category.subcategories[0].id);
                      }} className={`flex items-center px-3 py-2 w-full text-left text-sm font-medium rounded-md ${activeCategory === category.id ? 'bg-green-50 text-green-700' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-3846">
                            <span className="mr-3" data-id="element-3847">{category.icon}</span>
                            {category.name}
                          </button>
                          {activeCategory === category.id && <div className="pl-10 space-y-1" data-id="element-3848">
                              {category.subcategories.map(subcategory => {
                          // Skip subcategory if user doesn't have permission
                          if (!hasPermission(subcategory.permission)) return null;
                          return <button key={subcategory.id} onClick={() => setActiveSubcategory(subcategory.id)} className={`block px-3 py-2 w-full text-left text-sm rounded-md ${activeSubcategory === subcategory.id ? 'bg-gray-100 font-medium text-gray-900' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-3849">
                                    {subcategory.name}
                                  </button>;
                        })}
                            </div>}
                        </div>;
                  })}
                  </nav>
                </div>
              </div>
              {/* Help Box */}
              <div className="bg-white rounded-lg shadow p-4" data-id="element-3850">
                <div className="flex items-start" data-id="element-3851">
                  <div className="flex-shrink-0" data-id="element-3852">
                    <HelpCircleIcon size={20} className="text-blue-500" data-id="element-3853" />
                  </div>
                  <div className="ml-3" data-id="element-3854">
                    <h3 className="text-sm font-medium text-gray-900" data-id="element-3855">
                      Need help?
                    </h3>
                    <p className="mt-1 text-sm text-gray-500" data-id="element-3856">
                      Check our documentation for detailed information about
                      settings and configurations.
                    </p>
                    <a href="#" className="mt-2 text-sm text-blue-600 hover:text-blue-500 flex items-center" data-id="element-3857">
                      View Documentation
                      <ExternalLinkIcon size={14} className="ml-1" data-id="element-3858" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
            {/* Settings Content */}
            <div className="lg:w-3/4 xl:w-4/5" data-id="element-3859">
              <div className="bg-white rounded-lg shadow" data-id="element-3860">
                <div className="p-4 border-b border-gray-200" data-id="element-3861">
                  <div className="flex justify-between items-center" data-id="element-3862">
                    <div data-id="element-3863">
                      <h2 className="text-lg font-medium text-gray-900" data-id="element-3864">
                        {getCurrentSubcategory()?.name || 'Settings'}
                      </h2>
                      <p className="text-sm text-gray-500 mt-1" data-id="element-3865">
                        {getCurrentSubcategory()?.description || 'Configure system settings'}
                      </p>
                    </div>
                    {!canEditCurrentSettings() && <div className="flex items-center text-sm text-orange-600 bg-orange-50 px-3 py-1 rounded-md" data-id="element-3866">
                        <LockIcon size={16} className="mr-1" data-id="element-3867" />
                        Read-only
                      </div>}
                  </div>
                </div>
                <div className="p-4" data-id="element-3868">
                  {/* Settings List */}
                  <div className="space-y-6" data-id="element-3869">
                    {getCurrentSettings().length > 0 ? getCurrentSettings().map(setting => <div key={setting.id} className="border-b border-gray-200 pb-5 last:border-0 last:pb-0" data-id="element-3870">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between" data-id="element-3871">
                            <div className="mb-3 sm:mb-0" data-id="element-3872">
                              <label className="text-sm font-medium text-gray-900" data-id="element-3873">
                                {setting.name}
                                {setting.isRequired && <span className="text-red-500 ml-1" data-id="element-3874">*</span>}
                              </label>
                              <p className="text-sm text-gray-500 mt-1" data-id="element-3875">
                                {setting.description}
                              </p>
                            </div>
                            <div className="sm:w-1/2 lg:w-1/3" data-id="element-3876">
                              {renderSettingInput(setting)}
                              {setting.isSensitive && <p className="mt-1 text-xs text-gray-500 flex items-center" data-id="element-3877">
                                  <LockIcon size={12} className="mr-1" data-id="element-3878" />
                                  Sensitive information
                                </p>}
                            </div>
                          </div>
                        </div>) : <div className="text-center py-12" data-id="element-3879">
                        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100" data-id="element-3880">
                          <AlertCircleIcon size={24} className="text-gray-400" data-id="element-3881" />
                        </div>
                        <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-3882">
                          No settings found
                        </h3>
                        <p className="mt-1 text-sm text-gray-500" data-id="element-3883">
                          {searchQuery ? 'No settings match your search criteria.' : 'No settings are available in this category.'}
                        </p>
                      </div>}
                  </div>
                </div>
                {/* Settings Actions */}
                {getCurrentSettings().length > 0 && <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 rounded-b-lg" data-id="element-3884">
                    <button onClick={saveSettings} disabled={!hasChanges || isSaving || !canEditCurrentSettings()} className={`inline-flex justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${!hasChanges || !canEditCurrentSettings() ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`} data-id="element-3885">
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>}
              </div>
              {/* Info Cards */}
              {activeCategory === 'security' && <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3886">
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-md" data-id="element-3887">
                    <div className="flex items-start" data-id="element-3888">
                      <InfoIcon size={20} className="text-blue-500 mr-2 mt-0.5" data-id="element-3889" />
                      <div data-id="element-3890">
                        <h3 className="text-sm font-medium text-blue-800" data-id="element-3891">
                          Security Best Practices
                        </h3>
                        <p className="text-sm text-blue-700 mt-1" data-id="element-3892">
                          Enable two-factor authentication and set strong
                          password policies to enhance security.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-md" data-id="element-3893">
                    <div className="flex items-start" data-id="element-3894">
                      <AlertTriangleIcon size={20} className="text-yellow-500 mr-2 mt-0.5" data-id="element-3895" />
                      <div data-id="element-3896">
                        <h3 className="text-sm font-medium text-yellow-800" data-id="element-3897">
                          Audit Logging
                        </h3>
                        <p className="text-sm text-yellow-700 mt-1" data-id="element-3898">
                          Enabling audit logging helps track user actions and is
                          essential for compliance requirements.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>}
              {activeCategory === 'payments' && <div className="mt-6 bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-md" data-id="element-3899">
                  <div className="flex items-start" data-id="element-3900">
                    <AlertOctagonIcon size={20} className="text-yellow-500 mr-2 mt-0.5" data-id="element-3901" />
                    <div data-id="element-3902">
                      <h3 className="text-sm font-medium text-yellow-800" data-id="element-3903">
                        Payment Gateway Configuration
                      </h3>
                      <p className="text-sm text-yellow-700 mt-1" data-id="element-3904">
                        Ensure that you have the correct API keys and
                        credentials for your payment gateways. Test thoroughly
                        before switching to live mode.
                      </p>
                    </div>
                  </div>
                </div>}
            </div>
          </div>
        </main>
      </div>
      {/* Restore Defaults Modal */}
      {showRestoreModal && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-3905">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full" data-id="element-3906">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-3907">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-3908">
                Restore Default Settings
              </h3>
            </div>
            <div className="p-6" data-id="element-3909">
              <div className="flex items-center mb-4" data-id="element-3910">
                <div className="flex-shrink-0 bg-yellow-100 rounded-full p-2 mr-3" data-id="element-3911">
                  <AlertTriangleIcon size={24} className="text-yellow-600" data-id="element-3912" />
                </div>
                <div data-id="element-3913">
                  <h3 className="text-sm font-medium text-gray-900" data-id="element-3914">
                    Are you sure you want to restore default settings?
                  </h3>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-3915">
                    This will reset all settings to their default values. This
                    action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
            <div className="px-6 py-3 border-t border-gray-200 flex justify-end space-x-3" data-id="element-3916">
              <button onClick={() => setShowRestoreModal(false)} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-3917">
                Cancel
              </button>
              <button onClick={resetSettings} className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700" data-id="element-3918">
                Restore Defaults
              </button>
            </div>
          </div>
        </div>}
    </div>;
};
export default Settings;